<template>
  <section
    v-if="authorName"
    class="tw-w-full tw-min-w-0 tw-overflow-hidden tw-border-light-gray-2 tw-py-2 tw-text-left md:tw-py-3"
    :class="[authorPath ? 'tw-cursor-pointer' : '']"
    :role="authorPath ? 'link' : undefined"
    :tabindex="authorPath ? 0 : undefined"
    @click="goToAuthor"
    @keydown.enter.prevent="goToAuthor"
    @keydown.space.prevent="goToAuthor"
  >
    <div class="author-label tw-max-w-full tw-text-[12px] tw-leading-[18px] tw-text-dark-gray md:tw-text-mb-body-small">
      {{ $t("global.author") }}
    </div>

    <button
      v-if="authorPath"
      type="button"
      :title="authorName"
      class="tw-mt-1.5 tw-grid tw-w-full tw-min-w-0 tw-grid-cols-[40px_minmax(0,1fr)] tw-items-center tw-gap-2.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-no-underline tw-transition-opacity hover:tw-opacity-80 md:tw-grid-cols-[48px_minmax(0,1fr)] md:tw-gap-3"
    >
      <img
        v-if="imageSrc && !imageLoadFailed"
        :src="imageSrc"
        :alt="authorName"
        class="author-image tw-aspect-square tw-h-10 tw-w-10 tw-rounded-full tw-object-cover md:tw-h-12 md:tw-w-12"
        loading="lazy"
        decoding="async"
        @error="imageLoadFailed = true"
      >
      <div
        v-else
        class="tw-flex tw-aspect-square tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-light-gray-1 tw-text-mb-title-5 tw-text-dark-gray md:tw-h-12 md:tw-w-12"
      >
        {{ initial }}
      </div>
      <div class="tw-min-w-0 tw-overflow-hidden">
        <h2 class="author-text tw-text-mb-body-small tw-font-medium tw-text-black md:tw-text-mb-body">
          {{ authorName }}
        </h2>
        <p
          v-if="author.bio"
          class="author-text author-bio tw-mt-0.5 tw-text-[12px] tw-leading-[18px] tw-text-dark-gray md:tw-text-mb-body-small"
        >
          {{ author.bio }}
        </p>
      </div>
    </button>

    <div
      v-else
      class="tw-mt-1.5 tw-grid tw-w-full tw-min-w-0 tw-grid-cols-[40px_minmax(0,1fr)] tw-items-center tw-gap-2.5 md:tw-grid-cols-[48px_minmax(0,1fr)] md:tw-gap-3"
    >
      <img
        v-if="imageSrc && !imageLoadFailed"
        :src="imageSrc"
        :alt="authorName"
        class="author-image tw-aspect-square tw-h-10 tw-w-10 tw-rounded-full tw-object-cover md:tw-h-12 md:tw-w-12"
        loading="lazy"
        decoding="async"
        @error="imageLoadFailed = true"
      >
      <div
        v-else
        class="tw-flex tw-aspect-square tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-light-gray-1 tw-text-mb-title-5 tw-text-dark-gray md:tw-h-12 md:tw-w-12"
      >
        {{ initial }}
      </div>
      <div class="tw-min-w-0 tw-overflow-hidden">
        <h2 class="author-text tw-text-mb-body-small tw-font-medium tw-text-black md:tw-text-mb-body">
          {{ authorName }}
        </h2>
        <p
          v-if="author.bio"
          class="author-text author-bio tw-mt-0.5 tw-text-[12px] tw-leading-[18px] tw-text-dark-gray md:tw-text-mb-body-small"
        >
          {{ author.bio }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  author: ArticleAuthor
  authorId?: string | null
}>(), {
  authorId: null,
});

const imageLoadFailed = ref(false);
const route = useRoute();
const { localePath } = useLanguage();
const { getPath } = useNavigationMenu();
const authorName = computed(() => props.author?.penName || props.author?.fullName || '');
const authorId = computed(() => props.author?._id || props.author?.id || props.authorId || '');
const articleMenuPath = computed(() => {
  const path = getPath({ componentName: 'Article' });
  if (path && path !== '/') return path.replace(/\/$/, '');

  const currentMenuSlug = String(route.params.slug || '');
  return currentMenuSlug ? localePath(`/${currentMenuSlug}`).replace(/\/$/, '') : '';
});
const authorPath = computed(() => {
  if (!authorId.value) return '';
  if (!articleMenuPath.value) return '';
  return `${articleMenuPath.value}/authors/${authorId.value}`;
});
const goToAuthor = () => {
  if (!authorPath.value) return;
  const cachedAuthor = useState<ArticleAuthor | null>(`author-profile-${authorId.value}`, () => null);
  cachedAuthor.value = props.author;
  navigateTo(authorPath.value);
};
const initial = computed(() => authorName.value.trim().charAt(0).toUpperCase());
const imageSrc = computed(() => {
  const image = props.author?.profileImage;
  const src = image?.medium || image?.large || image?.small || image?.tiny || image?.fileName || '';
  return src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/') ? src : '';
});
watch(imageSrc, () => {
  imageLoadFailed.value = false;
});
</script>

<style scoped>
.author-text {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.author-label {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.author-image {
  object-position: center;
}

.author-bio {
  display: -webkit-box;
  overflow: hidden;
  line-height: 1.625;
  max-height: 3.25em;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
